'use client';

import { useCallback, useMemo } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import type { EvidenceTier, ArticleKind, Topic } from '@/lib/types';
import { GOAL_CANONICAL_LABEL, type GoalSlug } from '@/lib/aviado/goals';

// ---- Guards — filter out stray URL params so we don't render "Topic: bogus". ----

const TIER_SET = new Set<EvidenceTier>([
  'strong',
  'moderate',
  'preliminary',
  'insufficient',
]);
const KIND_SET = new Set<ArticleKind>([
  'knowledge-base',
  'evidence-dossier',
  'protocol-brief',
  'mechanism',
  'dose',
  'safety',
  'evidence-summary',
  'biomarker-impact',
  'comparison',
  'responder',
  'explainer',
  'qa',
]);
const TOPIC_SET = new Set<Topic>(['athletic', 'stress', 'safety', 'methodology', 'proofmark']);
const TIME_SET = new Set<TimeBucket>(['<5', '5-15', '15+']);
const SORT_SET = new Set<SortKey>(['newest', 'oldest', 'sources', 'shortest']);
const GOAL_SET = new Set<GoalSlug>(Object.keys(GOAL_CANONICAL_LABEL) as GoalSlug[]);

/**
 * useBrowseFilters — hook that owns URL serialization for the Browse page.
 *
 * Filter state lives in search params so every filter combo is shareable
 * via URL and back/forward navigation works for free.
 *
 * Recognized params:
 *   q           — free-text search
 *   goal        — GoalSlug (v2.7 canonical)
 *   tier        — EvidenceTier
 *   kind        — ArticleKind
 *   time        — '<5' | '5-15' | '15+'
 *   supplement  — supplement key
 *   topic       — Topic
 *   sort        — 'newest' | 'oldest' | 'sources' | 'shortest' (default newest)
 *   focus       — 'search' (ephemeral: Cmd+K targets the search input)
 *
 * Uses `router.replace({ query })` with `{ scroll: false }` so chip toggles
 * don't snap the viewport to the top of the results list.
 */

export type TimeBucket = '<5' | '5-15' | '15+';
export type SortKey = 'newest' | 'oldest' | 'sources' | 'shortest';

export interface BrowseFilters {
  q: string;
  goal: GoalSlug | null;
  tier: EvidenceTier | null;
  kind: ArticleKind | null;
  time: TimeBucket | null;
  supplement: string | null;
  topic: Topic | null;
  sort: SortKey;
  focus: string | null;
}

const EMPTY: BrowseFilters = {
  q: '',
  goal: null,
  tier: null,
  kind: null,
  time: null,
  supplement: null,
  topic: null,
  sort: 'newest',
  focus: null,
};

function validate<T extends string>(raw: string | null, allowed: Set<T>): T | null {
  return raw && allowed.has(raw as T) ? (raw as T) : null;
}

function parseFilters(sp: URLSearchParams): BrowseFilters {
  const sortRaw = sp.get('sort');
  return {
    q: sp.get('q') ?? '',
    goal: validate<GoalSlug>(sp.get('goal'), GOAL_SET),
    tier: validate<EvidenceTier>(sp.get('tier'), TIER_SET),
    kind: validate<ArticleKind>(sp.get('kind'), KIND_SET),
    time: validate<TimeBucket>(sp.get('time'), TIME_SET),
    supplement: sp.get('supplement') || null,
    topic: validate<Topic>(sp.get('topic'), TOPIC_SET),
    sort: (sortRaw && SORT_SET.has(sortRaw as SortKey) ? (sortRaw as SortKey) : 'newest'),
    focus: sp.get('focus') || null,
  };
}

function toQueryString(next: Partial<BrowseFilters>): string {
  const sp = new URLSearchParams();
  if (next.q) sp.set('q', next.q);
  if (next.goal) sp.set('goal', next.goal);
  if (next.tier) sp.set('tier', next.tier);
  if (next.kind) sp.set('kind', next.kind);
  if (next.time) sp.set('time', next.time);
  if (next.supplement) sp.set('supplement', next.supplement);
  if (next.topic) sp.set('topic', next.topic);
  if (next.sort && next.sort !== 'newest') sp.set('sort', next.sort);
  // `focus` is intentionally ephemeral — never persisted back to the URL.
  return sp.toString();
}

export function useBrowseFilters() {
  const router = useRouter();
  const pathname = usePathname() ?? '/browse';
  const searchParams = useSearchParams();
  const filters = useMemo(
    () => parseFilters(new URLSearchParams(searchParams?.toString() ?? '')),
    [searchParams]
  );

  const set = useCallback(
    (patch: Partial<BrowseFilters>) => {
      const next = { ...filters, ...patch };
      const qs = toQueryString(next);
      router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
    },
    [filters, pathname, router]
  );

  const clear = useCallback(() => {
    router.replace(pathname, { scroll: false });
  }, [pathname, router]);

  const clearOne = useCallback(
    (key: keyof BrowseFilters) => {
      const reset: Partial<BrowseFilters> =
        key === 'sort' ? { sort: 'newest' } : key === 'q' ? { q: '' } : { [key]: null };
      set(reset);
    },
    [set]
  );

  return { filters, set, clear, clearOne };
}

export { EMPTY as EMPTY_FILTERS };
